import React, { useState } from 'react';
import { Form, Radio, Divider } from 'antd';
import styled from 'styled-components';

import SerpPreview from './SerpPreview';
import OgCardPreview from './OgCardPreview';
import SeoChecklist from './SeoChecklist';
import { scoreSeoMeta } from './seoScore';

const Container = styled.div`
  flex: 11;
  min-width: 420px;

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #999;
    margin-bottom: 6px;
  }
`;

/**
 * Live previews for the Page SEO drawer, rendered as `createEndChildren` so it sits
 * beside the generated form fields and reads them as they are typed.
 *
 * Locales are discovered from the form's own `title__<code>` write fields rather than
 * hardcoded, so adding a Locale row on the backend surfaces here without a change.
 */
const SeoPagePreviews = () => {
  const form = Form.useFormInstance();
  // `[]` watches the whole store, which is what lets the locale set be dynamic.
  const values: Record<string, any> = (Form.useWatch([], form) as any) || {};
  const [locale, setLocale] = useState<string | undefined>();

  const localeCodes = Object.keys(values)
    .map(key => /^title__(.+)$/.exec(key)?.[1])
    .filter((code): code is string => !!code)
    .sort();

  const activeLocale = locale && localeCodes.includes(locale) ? locale : localeCodes[0];

  const title = activeLocale ? values[`title__${activeLocale}`] : undefined;
  const description = activeLocale ? values[`description__${activeLocale}`] : undefined;

  const result = scoreSeoMeta({
    title,
    description,
    ogImage: values.og_image,
    focusKeyword: values.focus_keyword,
    noindex: values.noindex,
  });

  return (
    <Container>
      {localeCodes.length > 1 && (
        <Radio.Group
          size="small"
          value={activeLocale}
          onChange={event => setLocale(event.target.value)}
          style={{ marginBottom: 16 }}
        >
          {localeCodes.map(code => (
            <Radio.Button key={code} value={code}>
              {code.toUpperCase()}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}

      <SeoChecklist result={result} />

      <Divider style={{ margin: '18px 0 14px' }} />

      <div className="section-label">Google result</div>
      <SerpPreview
        title={title}
        description={description}
        path={values.path}
        locale={activeLocale}
      />

      <div className="section-label" style={{ marginTop: 18 }}>
        Shared link
      </div>
      <OgCardPreview title={title} description={description} imageUrl={values.og_image} />
    </Container>
  );
};

export default SeoPagePreviews;
